/**
 * Check-In Dialog Component
 * Handles guest check-in with two-step room selection and registration card print
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { LogIn, Info, Bed, DoorOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export const CheckInDialog = ({
  open,
  onOpenChange,
  guest,
  onConfirm,
  loading,
  hotelId
}) => {
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [selectedRoomNumber, setSelectedRoomNumber] = useState('');
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [checkedInGuest, setCheckedInGuest] = useState(null);

  // Get unique room types
  const roomTypes = [...new Set(availableRooms.map(r => r.name))];
  
  // Get available room numbers for selected type
  const availableRoomNumbers = availableRooms
    .filter(r => r.name === selectedRoomType && r.room_number)
    .map(r => ({
      number: r.room_number,
      id: r.id,
      status: r.room_status
    }));

  useEffect(() => {
    if (open && hotelId) {
      loadAvailableRooms();
    }
  }, [open, hotelId]);

  useEffect(() => {
    // Pre-select room type from guest's reservation
    if (guest?.room_type && availableRooms.length > 0) {
      setSelectedRoomType(guest.room_type);
    }
  }, [guest, availableRooms]);

  const loadAvailableRooms = async () => {
    try {
      setLoadingRooms(true);
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/available-rooms`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch rooms');
      }
      
      const data = await response.json();
      
      // Filter only clean/ready rooms that have room numbers
      const cleanRooms = (data.rooms || []).filter(room => 
        (room.room_status === 'clean' || room.room_status === 'inspected') && room.room_number
      );
      
      setAvailableRooms(cleanRooms);
    } catch (error) {
      console.error('Error loading available rooms:', error);
      toast.error('Failed to load available rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedRoomType) {
      toast.error('Please select a room type');
      return;
    }
    if (!selectedRoomNumber) {
      toast.error('Please select a room number');
      return;
    }
    
    try {
      await onConfirm(selectedRoomNumber);
      setCheckedInGuest({
        ...guest,
        assigned_room_number: selectedRoomNumber,
        room_type: selectedRoomType
      });
      setShowSuccess(true);
    } catch (error) {
      console.error('Check-in failed:', error);
    }
  };

  const handlePrintRegistrationCard = () => {
    const printWindow = window.open('', '_blank');
    const regCardHtml = generateRegistrationCardHTML();
    
    printWindow.document.write(regCardHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    
    toast.success('Registration card sent to printer');
  };

  const generateRegistrationCardHTML = () => {
    const today = new Date().toLocaleDateString();
    // Calculate room rate per night
    const roomRate = checkedInGuest?.room_rate || checkedInGuest?.final_price / (checkedInGuest?.nights || 1) || 0;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Registration Card - ${checkedInGuest?.guest_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', system-ui, sans-serif; 
            padding: 30px;
            background: #f5f5f5;
          }
          .card-wrapper {
            max-width: 850px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
            color: white;
            padding: 50px 40px;
            text-align: center;
            position: relative;
            box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
          }
          .hotel-logo {
            width: 80px;
            height: 80px;
            margin: 0 auto 15px;
            background: white;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .hotel-logo img {
            max-width: 70px;
            max-height: 70px;
            object-fit: contain;
          }
          .hotel-logo-placeholder {
            font-size: 36px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            font-weight: 800;
          }
          .hotel-name {
            font-size: 32px;
            font-weight: 700;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }
          .card-title {
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 3px;
            text-transform: uppercase;
            opacity: 0.95;
          }
          .content {
            padding: 40px;
          }
          .section {
            margin-bottom: 35px;
          }
          .section-title {
            font-size: 13px;
            font-weight: 800;
            text-transform: uppercase;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            letter-spacing: 2px;
            margin-bottom: 25px;
            padding-bottom: 12px;
            border-bottom: 3px solid #d1fae5;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
          .info-item {
            padding: 12px 0;
            border-bottom: 1px solid #e5e7eb;
          }
          .info-label {
            font-size: 11px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;
          }
          .info-value {
            font-size: 16px;
            font-weight: 600;
            color: #1f2937;
          }
          .info-item-full {
            padding: 12px 0;
            border-bottom: 1px solid #e5e7eb;
            grid-column: 1 / -1;
          }
          .room-badge {
            display: inline-block;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 8px 20px;
            border-radius: 6px;
            font-size: 24px;
            font-weight: 700;
            letter-spacing: 1px;
          }
          .rate-badge {
            display: inline-block;
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            color: white;
            padding: 6px 16px;
            border-radius: 6px;
            font-size: 18px;
            font-weight: 700;
          }
          .signature-section {
            margin-top: 50px;
            padding-top: 30px;
            border-top: 2px solid #e5e7eb;
          }
          .sig-label {
            font-size: 12px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 15px;
          }
          .sig-line {
            border-bottom: 2px solid #1f2937;
            width: 400px;
            margin: 20px 0;
          }
          .sig-note {
            font-size: 11px;
            color: #9ca3af;
            font-style: italic;
          }
          .terms {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            padding: 25px;
            border-radius: 12px;
            margin-top: 35px;
            border-left: 5px solid #10b981;
            box-shadow: 0 2px 10px rgba(16, 185, 129, 0.1);
          }
          .terms-title {
            font-size: 11px;
            font-weight: 700;
            color: #374151;
            margin-bottom: 10px;
          }
          .terms-text {
            font-size: 10px;
            color: #6b7280;
            line-height: 1.6;
          }
          .footer {
            background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
            color: white;
            padding: 35px 40px;
            text-align: center;
            border-top: none;
          }
          .footer-text {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.8;
          }
          @media print {
            body { background: white; padding: 0; }
            .card-wrapper { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="card-wrapper">
          <div class="header">
            <div class="hotel-logo">
              <!-- Hotel can replace this with: <img src="YOUR_LOGO_URL" alt="Hotel Logo"> -->
              <div class="hotel-logo-placeholder">🏨</div>
            </div>
            <div class="hotel-name">The Grand Marina Hotel</div>
            <div class="card-title">Guest Registration Card</div>
          </div>

          <div class="content">
            <div class="section">
              <div class="section-title">Guest Information</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Full Name</div>
                  <div class="info-value">${checkedInGuest?.guest_name || ''}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Email Address</div>
                  <div class="info-value">${checkedInGuest?.guest_email || ''}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Phone Number</div>
                  <div class="info-value">${checkedInGuest?.guest_phone || 'Not provided'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Registration Date</div>
                  <div class="info-value">${today}</div>
                </div>
                <div class="info-item-full">
                  <div class="info-label">Address</div>
                  <div class="info-value">${checkedInGuest?.address || checkedInGuest?.guest_address || 'Not provided'}</div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Accommodation Details</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Room Number</div>
                  <div class="info-value">
                    <span class="room-badge">${checkedInGuest?.assigned_room_number || ''}</span>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">Room Type</div>
                  <div class="info-value">${checkedInGuest?.room_type || ''}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Check-In Date</div>
                  <div class="info-value">${checkedInGuest?.check_in || ''}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Check-Out Date</div>
                  <div class="info-value">${checkedInGuest?.check_out || ''}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Duration of Stay</div>
                  <div class="info-value">${checkedInGuest?.nights || ''} Nights</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Room Rate (Per Night)</div>
                  <div class="info-value">
                    <span class="rate-badge">$${roomRate.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="terms">
              <div class="terms-title">Terms & Conditions</div>
              <div class="terms-text">
                By signing below, I acknowledge that I have read and agree to the hotel's terms and conditions. 
                I understand that I am responsible for all charges incurred during my stay, including room charges, 
                taxes, and any additional services or amenities used.
              </div>
            </div>

            <div class="signature-section">
              <div class="sig-label">Guest Signature</div>
              <div class="sig-line"></div>
              <div class="sig-note">I agree to the terms and conditions of my stay</div>
            </div>
          </div>

          <div class="footer">
            <div class="footer-text">
              <strong>The Grand Marina Hotel</strong><br>
              Dubai, UAE | contact@grandmarina.com | +971 4 XXX XXXX<br>
              Thank you for choosing The Grand Marina Hotel. We wish you a pleasant stay!
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleClose = () => {
    setSelectedRoomType('');
    setSelectedRoomNumber('');
    setShowSuccess(false);
    setCheckedInGuest(null);
    onOpenChange(false);
  };

  return (
    <>
      {/* Main Check-In Dialog - Modernized */}
      <Dialog open={open && !showSuccess} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="checkin-dialog">
          {/* Modern Gradient Header */}
          <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-600 p-3 rounded-xl shadow-lg">
                <LogIn className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-white mb-1">
                  Check In Guest
                </DialogTitle>
                <p className="text-white/90 text-sm">Assign room and complete check-in</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {guest && (
              <>
                {/* Guest Info Card */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl shadow-lg">
                      {guest.guest_name?.charAt(0).toUpperCase() || 'G'}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {guest.guest_name}
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 font-medium">Check-In:</span>
                          <span className="ml-2 text-gray-900 font-semibold">{guest.check_in}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Check-Out:</span>
                          <span className="ml-2 text-gray-900 font-semibold">{guest.check_out}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Nights:</span>
                          <span className="ml-2 text-gray-900 font-semibold">{guest.nights}</span>
                        </div>
                        {guest.room_type && (
                          <div>
                            <span className="text-gray-500 font-medium">Booked:</span>
                            <span className="ml-2 text-blue-600 font-semibold">{guest.room_type}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-5">
                  <div className="flex items-center gap-2 text-lg font-bold text-gray-900">
                    <Bed className="w-5 h-5 text-emerald-600" />
                    Assign Room
                  </div>
                  
                  {loadingRooms ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-3" />
                      <span className="text-sm text-gray-600 font-medium">Loading available rooms...</span>
                    </div>
                  ) : availableRooms.length === 0 ? (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
                      <div className="text-red-600 mb-2">⚠️ No Clean Rooms Available</div>
                      <p className="text-sm text-red-700">Please check the room board or clean a room first.</p>
                    </div>
                  ) : (
                    <>
                      {/* Step 1: Room Type */}
                      <div className="space-y-3 p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-300 transition-colors">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <span className="bg-emerald-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                            Room Type
                          </Label>
                          <Badge className="bg-blue-500/10 text-blue-600 border-blue-300">From Reservation</Badge>
                        </div>
                        <Select value={selectedRoomType} onValueChange={(value) => {
                          setSelectedRoomType(value);
                          setSelectedRoomNumber('');
                        }}>
                          <SelectTrigger className="h-12 text-base font-medium" data-testid="room-type-select">
                            <SelectValue placeholder="Choose room type" />
                          </SelectTrigger>
                          <SelectContent>
                            {roomTypes.map((typeName) => {
                              const roomsOfType = availableRooms.filter(r => r.name === typeName);
                              const availableCount = roomsOfType.length;
                              const room = roomsOfType[0];
                              const isReservedType = typeName === guest?.room_type;
                              
                              return (
                                <SelectItem key={typeName} value={typeName} className="py-3">
                                  <div className="flex items-center justify-between w-full gap-6">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-base">{typeName}</span>
                                      {isReservedType && (
                                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-300 text-xs">
                                          Booked
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-300">
                                        {availableCount} available
                                      </Badge>
                                      <span className="font-semibold text-gray-700">
                                        ${room.price_per_night}/night
                                      </span>
                                    </div>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {selectedRoomType && (
                          <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
                            {selectedRoomType === guest?.room_type 
                              ? "✓ Matching reserved room type"
                              : `⚠️ Guest reserved ${guest?.room_type || "different type"}. Confirm before proceeding.`}
                          </p>
                        )}
                      </div>

                      {/* Step 2: Room Number (only show when type is selected) */}
                      {selectedRoomType && (
                        <div className="space-y-3 p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-300 transition-colors">
                          <Label className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <span className="bg-emerald-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                            <DoorOpen className="w-5 h-5 text-emerald-600" />
                            Select Room Number
                            <span className="text-sm font-normal text-gray-500">({availableRoomNumbers.length} available)</span>
                          </Label>
                          <Select value={selectedRoomNumber} onValueChange={setSelectedRoomNumber}>
                            <SelectTrigger className="h-12 text-base font-medium" data-testid="room-number-select">
                              <SelectValue placeholder="Choose room number" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableRoomNumbers.map((room) => (
                                <SelectItem key={room.id} value={room.number} className="py-3">
                                  <div className="flex items-center justify-between w-full gap-4">
                                    <span className="font-bold text-xl text-blue-600">Room {room.number}</span>
                                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-300">
                                      {room.status === 'clean' ? '✓ Clean & Ready' : '✓ Ready'}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Selected Room Confirmation */}
                          {selectedRoomNumber && (
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-5 mt-4">
                              <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                                  {selectedRoomNumber}
                                </div>
                                <div className="flex-1">
                                  <p className="font-bold text-emerald-900 text-lg">Room {selectedRoomNumber} Selected</p>
                                  <p className="text-emerald-700 font-medium">{selectedRoomType}</p>
                                  <p className="text-sm text-emerald-600 mt-1">✓ Ready for guest arrival</p>
                                </div>
                                <Bed className="w-8 h-8 text-emerald-600" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={loading} className="px-6">
              Cancel
            </Button>
            <Button 
              onClick={handleCheckIn} 
              disabled={!selectedRoomType || !selectedRoomNumber || loading}
              data-testid="confirm-checkin-btn"
              className="px-8 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-semibold shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Confirm Check-In
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog - Modernized */}
      <Dialog open={showSuccess} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <LogIn className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-white">
                  Check-In Successful! 🎉
                </DialogTitle>
                <p className="text-white/90 text-sm">Guest is ready to enjoy their stay</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Guest Check-In Confirmation */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-emerald-500 to-green-500 text-white w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg">
                  {checkedInGuest?.assigned_room_number}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-emerald-900 text-xl mb-2">{checkedInGuest?.guest_name}</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-emerald-600 font-medium">Room:</span>
                      <span className="ml-2 text-emerald-900 font-bold text-lg">{checkedInGuest?.assigned_room_number}</span>
                    </div>
                    <div>
                      <span className="text-emerald-600 font-medium">Type:</span>
                      <span className="ml-2 text-emerald-900 font-semibold">{checkedInGuest?.room_type}</span>
                    </div>
                    <div>
                      <span className="text-emerald-600 font-medium">Check-In:</span>
                      <span className="ml-2 text-emerald-900 font-semibold">{checkedInGuest?.check_in}</span>
                    </div>
                    <div>
                      <span className="text-emerald-600 font-medium">Check-Out:</span>
                      <span className="ml-2 text-emerald-900 font-semibold">{checkedInGuest?.check_out}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-xl p-5">
              <p className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Next Steps:
              </p>
              <ol className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span className="text-gray-700">Print registration card for guest signature</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span className="text-gray-700">Provide room key and welcome amenities</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span className="text-gray-700">Welcome guest and explain hotel facilities</span>
                </li>
              </ol>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} className="px-6">
              Close
            </Button>
            <Button 
              onClick={handlePrintRegistrationCard} 
              className="px-8 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <polyline points="6 9 6 2 18 2 18 9"></polyline>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <rect x="6" y="14" width="12" height="8"></rect>
              </svg>
              Print Registration Card
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
